// Мини-задания правил A2, часть 1: уроки a2-l004 … a2-l030 (тикет 06 .scratch/grammar-unify).
// Слова и грамматика — только пройденные к уроку правила (линия .scratch/a2-rework/spec.md).
import type { BreakdownPart } from '../exercise'
import type { MiniSet } from './types'

// Основа в «Собери слово»: дана, выбирать нечего.
const stem = (text: string) => ({ answer: text, options: [], label: '', why: '' })
// Часть разбора: основа или окружение — без метки, морфема / слово-правило — с меткой.
const p = (text: string, label = '', why = ''): BreakdownPart => ({ text, label, why })
const sp = p(' ')

export const MINI_A2_1: MiniSet[] = [
  // a2-l004 (A2-4): n после «его / их» перед «где, куда, откуда»; винительный - с урока 7.
  {
    rule: 'possessive-n-buffer',
    explain:
      'После "его" (-ı, -sı) и "их" (-ları) перед падежом появляется n: ev-i-n-de "в его доме", anne-si-n-e "его маме", oda-sı-n-dan "из его комнаты", ev-leri-n-de "в их доме". После "мой" и "твой" её нет: evimde, annene.',
    examples: [
      {
        parts: [p('Ali '), p('ev'), p('i', 'его', 'после согласной "его" - -i'), p('n', 'связка n', 'после "его" перед падежом'), p('de', 'где?', '-de'), p(' çalışıyor.')],
        ru: 'Али работает у себя дома.',
      },
      {
        parts: [p('Ayşe '), p('anne'), p('si', 'её', 'после гласной "его, её" - -si'), p('n', 'связка n', ''), p('e', 'кому?', '-e'), p(' çiçek aldı.')],
        ru: 'Айше купила маме цветы.',
      },
    ],
    items: [
      {
        kind: 'affix', base: 'ev', baseRu: 'дом', task: 'его · где?',
        context: { tr: 'Ali bugün ___ çalışıyor.', ru: 'Али сегодня работает у себя дома.' },
        slots: [stem('ev'), { answer: 'i', options: ['i', 'im', 'in'], label: 'его', why: 'после согласной "его" - -i' }, { answer: 'nde', options: ['nde', 'de', 'yde'], label: 'где?', why: 'после "его" перед падежом - n' }],
        answer: 'evinde', d: 1,
      },
      {
        kind: 'affix', base: 'anne', baseRu: 'мама', task: 'её · кому?',
        context: { tr: 'Ayşe ___ çiçek aldı.', ru: 'Айше купила маме цветы.' },
        slots: [stem('anne'), { answer: 'si', options: ['si', 'i', 'm'], label: 'её', why: 'после гласной "его, её" - -si' }, { answer: 'ne', options: ['ne', 'ye', 'e'], label: 'кому?', why: 'после "его" - n, не y' }],
        answer: 'annesine', d: 1,
      },
      {
        kind: 'affix', base: 'oda', baseRu: 'комната', task: 'его · откуда?',
        context: { tr: 'Can ___ çıkıyor.', ru: 'Джан выходит из своей комнаты.' },
        slots: [stem('oda'), { answer: 'sı', options: ['sı', 'ı', 'm'], label: 'его', why: 'после гласной - -sı' }, { answer: 'ndan', options: ['ndan', 'dan', 'ydan'], label: 'откуда?', why: 'после "его" перед падежом - n' }],
        answer: 'odasından', d: 1,
      },
      {
        kind: 'affix', base: 'mutfak', baseRu: 'кухня', task: 'их · где?',
        context: { tr: 'Komşular ___ kahvaltı yapıyor.', ru: 'Соседи завтракают у себя на кухне.' },
        slots: [stem('mutfak'), { answer: 'ları', options: ['ları', 'leri', 'ı'], label: 'их', why: 'их - -ları, после a' }, { answer: 'nda', options: ['nda', 'da', 'ta'], label: 'где?', why: 'после "их" перед падежом - n' }],
        answer: 'mutfaklarında', d: 2,
      },
      {
        kind: 'affix', base: 'çocuk', baseRu: 'ребёнок', task: 'его · кому?',
        context: { tr: 'Annem ___ süt veriyor.', ru: 'Мама даёт своему ребёнку молоко.' },
        slots: [stem('çocuğ'), { answer: 'u', options: ['u', 'su', 'ü'], label: 'его', why: 'k перед гласной - ğ, "его" после согласной - -u' }, { answer: 'na', options: ['na', 'ya', 'a'], label: 'кому?', why: 'после "его" - n' }],
        answer: 'çocuğuna', d: 2,
      },
      {
        kind: 'affix', base: 'ev', baseRu: 'дом', task: 'мой · где?',
        context: { tr: 'Bugün ___ çalışıyorum.', ru: 'Сегодня я работаю дома.' },
        slots: [stem('ev'), { answer: 'im', options: ['im', 'i', 'in'], label: 'мой', why: '"мой" - -im' }, { answer: 'de', options: ['de', 'nde', 'yde'], label: 'где?', why: 'после "мой" n не нужна' }],
        answer: 'evimde', d: 2,
      },
      {
        kind: 'transform', source: 'Evimde çalışıyorum.', sourceRu: 'я работаю у себя дома', instruction: 'Скажите то же про Али: "Ali ..."',
        answer: 'Ali evinde çalışıyor.', variants: [],
        breakdown: [{ text: 'ev', label: '', why: '' }, { text: 'i', label: 'его', why: '"мой" -im меняем на "его" -i' }, { text: 'n', label: 'связка n', why: 'после "его" перед падежом' }, { text: 'de', label: 'где?', why: '' }], d: 3,
      },
      {
        kind: 'transform', source: 'Она купила маме цветы.', instruction: 'Скажите это по-турецки',
        answer: 'Annesine çiçek aldı.', variants: ['O annesine çiçek aldı.'],
        breakdown: [{ text: 'anne', label: '', why: '' }, { text: 'si', label: 'её', why: '' }, { text: 'n', label: 'связка n', why: 'после "его, её" перед падежом' }, { text: 'e', label: 'кому?', why: '' }], blankWhole: true, d: 3,
      },
      {
        kind: 'transform', source: 'Дети спят у себя в комнате.', instruction: 'Скажите это по-турецки',
        answer: 'Çocuklar odalarında uyuyor.', variants: [],
        breakdown: [{ text: 'oda', label: '', why: '' }, { text: 'ları', label: 'их', why: '' }, { text: 'n', label: 'связка n', why: 'после "их" перед падежом' }, { text: 'da', label: 'где?', why: '' }], blankWhole: true, d: 3,
      },
    ],
  },

  // a2-l009: вопросительные слова в падежах.
  {
    rule: 'question-words-cases',
    explain:
      'Вопросительное слово склоняется, как обычное существительное, и стоит в том же падеже, что и будущий ответ: Kime? - Anneme. Падеж выбирает глагол: "Кому звонишь?" - Kimi arıyorsun?, потому что aramak берёт -i.',
    examples: [
      { parts: [p('Kim'), p('e', 'кому?', 'как в ответе: Anneme'), p(' sordun?')], ru: 'У кого ты спросил? (sormak берёт -e)' },
      { parts: [p('Ne'), p('yi', 'что? (то самое)', 'после гласной - y, как odayı'), p(' seviyor?')], ru: 'Что ему нравится?' },
    ],
    items: [
      {
        kind: 'affix', base: 'kim', baseRu: 'кто', task: 'с кем?',
        context: { tr: '___ gidiyorsun?', ru: 'С кем ты едешь?' },
        slots: [stem('kim'), { answer: 'inle', options: ['inle', 'e', 'den'], label: 'с кем?', why: 'по образцу benimle: kiminle' }],
        answer: 'kiminle', d: 1,
      },
      {
        kind: 'affix', base: 'kim', baseRu: 'кто', task: 'кого?',
        context: { tr: '___ bekliyorsun?', ru: 'Кого ты ждёшь?' },
        slots: [stem('kim'), { answer: 'i', options: ['i', 'e', 'de'], label: 'кого? (винительный падеж)', why: 'bekle- берёт "кого?": Kimi? - Ablamı' }],
        answer: 'kimi', d: 1,
      },
      {
        kind: 'affix', base: 'kim', baseRu: 'кто', task: 'от кого?',
        context: { tr: '___ aldın?', ru: 'От кого ты получил?' },
        slots: [stem('kim'), { answer: 'den', options: ['den', 'e', 'de'], label: 'от кого?', why: 'ответ тоже с -den: Babamdan' }],
        answer: 'kimden', d: 1,
      },
      {
        kind: 'affix', base: 'ne', baseRu: 'что', task: 'что? (винительный падеж)',
        context: { tr: 'Kızın ___ seviyor?', ru: 'Что любит твоя дочь?' },
        slots: [stem('ne'), { answer: 'yi', options: ['yi', 'i', 'ye'], label: 'что? (винительный падеж)', why: 'после гласной - y, как odayı' }],
        answer: 'neyi', d: 1,
      },
      {
        kind: 'affix', base: 'kim', baseRu: 'кто', task: 'кому? (звонить)',
        context: { tr: '___ arıyorsun?', ru: 'Кому ты звонишь?' },
        slots: [stem('kim'), { answer: 'i', options: ['i', 'e', 'den'], label: 'кого? (винительный падеж)', why: 'aramak берёт -i: звонят "кого", а не "кому"' }],
        answer: 'kimi', d: 2,
      },
      {
        kind: 'affix', base: 'kim', baseRu: 'кто', task: 'у кого? (спросить)',
        context: { tr: '___ sordun?', ru: 'У кого ты спросил?' },
        slots: [stem('kim'), { answer: 'e', options: ['e', 'de', 'den'], label: 'кому?', why: 'sormak берёт -e: спрашивают "кому"' }],
        answer: 'kime', d: 2,
      },
      {
        kind: 'affix', base: 'hangi', baseRu: 'какой', task: 'который из них · что?',
        context: { tr: '___ istiyorsunuz?', ru: 'Который из них вы хотите?' },
        slots: [stem('hangi'), { answer: 'si', options: ['si', 'i', 'm'], label: 'из них', why: 'hangisi - когда предметы перед глазами' }, { answer: 'ni', options: ['ni', 'yi', 'i'], label: 'что? (винительный падеж)', why: 'как в evini: после -si перед падежом - n' }],
        answer: 'hangisini', d: 2,
      },
      {
        kind: 'transform', source: 'Anneme soruyorum.', sourceRu: 'я спрашиваю у мамы',
        instruction: 'Задайте вопрос к слову anneme: "У кого ты спрашиваешь?"',
        answer: 'Kime soruyorsun?', variants: ['Sen kime soruyorsun?'],
        breakdown: [p('Kim'), p('e', 'кому?', 'вопрос в том же падеже, что ответ: anneme')], d: 3,
      },
      {
        kind: 'transform', source: 'С кем ты едешь?', instruction: 'Скажите это по-турецки',
        answer: 'Kiminle gidiyorsun?', variants: ['Kimle gidiyorsun?', 'Sen kiminle gidiyorsun?'], blankWhole: true,
        breakdown: [p('Kim'), p('inle', 'с кем?', 'по образцу benimle; в разговоре и kimle')], d: 3,
      },
      {
        kind: 'transform', source: 'Кому ты звонишь?', instruction: 'Скажите это по-турецки',
        answer: 'Kimi arıyorsun?', variants: ['Sen kimi arıyorsun?'], blankWhole: true,
        breakdown: [p('Kim'), p('i', 'кого? (винительный падеж)', 'aramak берёт -i, не -e')], d: 3,
      },
    ],
  },

  // a2-l010: -den beri и -dır.
  {
    rule: 'since-den-beri-dir',
    explain:
      'С какого момента - -den beri: dünden beri, marttan beri. Сколько времени уже длится - -dır прямо на отрезке: iki yıldır, üç gündür. Глагол - в настоящем: İki yıldır bankada çalışıyorum.',
    examples: [
      { parts: [p('İki '), p('yıl'), p('dır', 'уже (сколько)', 'отрезок; после ı - -dır'), p(' bankada çalışıyorum.')], ru: 'Я уже два года работаю в банке.' },
      { parts: [p('Dün'), p('den', 'откуда', 'точка на календаре'), p(' beri', 'с тех пор', ''), p(' ateşim var.')], ru: 'У меня температура со вчерашнего дня.' },
    ],
    items: [
      {
        kind: 'affix', base: 'gün', baseRu: 'день', task: 'уже (сколько)',
        context: { tr: 'Üç ___ başım ağrıyor.', ru: 'Голова болит уже три дня.' },
        slots: [stem('gün'), { answer: 'dür', options: ['dür', 'dir', 'den'], label: 'уже (сколько)', why: 'отрезок - -dır; после ü - -dür' }],
        answer: 'gündür', d: 1,
      },
      {
        kind: 'affix', base: 'yıl', baseRu: 'год', task: 'уже (сколько)',
        context: { tr: 'İki ___ Türkiye\'deyim.', ru: 'Я уже два года в Турции.' },
        slots: [stem('yıl'), { answer: 'dır', options: ['dır', 'dir', 'dan'], label: 'уже (сколько)', why: 'после ı - -dır' }],
        answer: 'yıldır', d: 1,
      },
      {
        kind: 'affix', base: 'dün', baseRu: 'вчера', task: 'с какого момента',
        context: { tr: '___ beri ateşim var.', ru: 'У меня температура со вчерашнего дня.' },
        slots: [stem('dün'), { answer: 'den', options: ['den', 'dür', 'de'], label: 'откуда', why: 'точка на календаре - -den beri' }],
        answer: 'dünden', d: 1,
      },
      {
        kind: 'affix', base: 'mart', baseRu: 'март', task: 'с какого момента',
        context: { tr: '___ beri İstanbul\'dayım.', ru: 'Я в Стамбуле с марта.' },
        slots: [stem('mart'), { answer: 'tan', options: ['tan', 'dan', 'tır'], label: 'откуда', why: 'месяц - точка: -den beri; после глухой t - t' }],
        answer: 'marttan', d: 1,
      },
      {
        kind: 'affix', base: 'saat', baseRu: 'час', task: 'уже (сколько)',
        context: { tr: 'Bir ___ bekliyorum.', ru: 'Я жду уже час.' },
        slots: [stem('saat'), { answer: 'tir', options: ['tir', 'tır', 'ten'], label: 'уже (сколько)', why: 'saat - как мягкое: -tir; после глухой t - t' }],
        answer: 'saattir', d: 2,
      },
      {
        kind: 'affix', base: 'öğlen', baseRu: 'полдень', task: 'с какого момента',
        context: { tr: '___ beri çalışıyorum.', ru: 'Я работаю с обеда.' },
        slots: [stem('öğlen'), { answer: 'den', options: ['den', 'dir', 'e'], label: 'откуда', why: 'полдень - точка, а не отрезок: -den beri' }],
        answer: 'öğlenden', d: 2,
      },
      {
        kind: 'affix', base: 'ay', baseRu: 'месяц', task: 'уже (сколько)',
        context: { tr: 'Üç ___ bankada çalışıyorum.', ru: 'Я уже три месяца работаю в банке.' },
        slots: [stem('ay'), { answer: 'dır', options: ['dır', 'dan', 'dir'], label: 'уже (сколько)', why: 'три месяца - отрезок: -dır; после a - -dır' }],
        answer: 'aydır', d: 2,
      },
      {
        kind: 'transform', source: 'İki yıl bankada çalıştım.', sourceRu: 'я два года проработал в банке (и ушёл)',
        instruction: 'Скажите, что работаете там до сих пор: "Я уже два года работаю в банке"',
        answer: 'İki yıldır bankada çalışıyorum.', variants: [],
        breakdown: [p('yıl'), p('dır', 'уже (сколько)', 'и глагол в настоящем -yor, не -di')], d: 2,
      },
      {
        kind: 'transform', source: 'С каких пор ты ждёшь?', instruction: 'Скажите это по-турецки',
        answer: 'Ne zamandan beri bekliyorsun?', variants: [], blankWhole: true,
        breakdown: [p('zaman'), p('dan', 'откуда', 'вопрос о точке: ne zamandan beri')], d: 3,
      },
      {
        kind: 'transform', source: 'Давно не виделись!', instruction: 'Скажите это по-турецки',
        answer: 'Uzun zamandır görüşmedik!', variants: [], blankWhole: true,
        breakdown: [p('zaman'), p('dır', 'уже (сколько)', 'отрезок: "долгое время"')], d: 3,
      },
    ],
  },

  // a2-l011: частица de / da «тоже».
  {
    rule: 'particle-de-da-harmony',
    explain:
      'De / da - это "тоже": Ben de "я тоже", O da "он тоже". Пишется отдельным словом, но гласную берёт у слова перед собой: после e, i, ö, ü - de, после a, ı, o, u - da. Ставится сразу после того, к чему относится.',
    examples: [
      { parts: [p('Ben'), sp, p('de', 'тоже', 'после e - de, отдельным словом'), p(' geliyorum.')], ru: 'Я тоже иду.' },
      { parts: [p('Ablam'), sp, p('da', 'тоже', 'после a - da'), p(' doktor.')], ru: 'Моя сестра тоже врач.' },
    ],
    items: [
      {
        kind: 'transform', source: 'Ben geliyorum.', sourceRu: 'я иду',
        instruction: 'Добавьте "тоже": "Я тоже иду"',
        answer: 'Ben de geliyorum.', variants: [],
        breakdown: [p('Ben'), sp, p('de', 'тоже', 'после e - de; пишется отдельно')], d: 1,
      },
      {
        kind: 'transform', source: 'Ablam doktor.', sourceRu: 'моя сестра - врач',
        instruction: 'Добавьте "тоже": "Моя сестра тоже врач"',
        answer: 'Ablam da doktor.', variants: [],
        breakdown: [p('Ablam'), sp, p('da', 'тоже', 'после a - da')], d: 1,
      },
      {
        kind: 'transform', source: 'Elif geliyor.', sourceRu: 'Элиф идёт',
        instruction: 'Добавьте "тоже": "Элиф тоже идёт"',
        answer: 'Elif de geliyor.', variants: [],
        breakdown: [p('Elif'), sp, p('de', 'тоже', 'последняя гласная i - de')], d: 1,
      },
      {
        kind: 'affix', base: 'ben', baseRu: 'я', task: 'у кого? (-de слитно)',
        context: { tr: 'Anahtar ___.', ru: 'Ключ у меня.' },
        slots: [stem('ben'), { answer: 'de', options: ['de', 'da', 'te'], label: 'у кого? где?', why: 'это не "тоже", а "у меня" - пишется слитно: bende' }],
        answer: 'bende', d: 2,
      },
      {
        kind: 'transform', source: 'Mutfak büyük.', sourceRu: 'кухня большая',
        instruction: 'Скажите "И кухня большая"',
        answer: 'Mutfak da büyük.', variants: [],
        breakdown: [p('Mutfak'), sp, p('da', 'и, тоже', 'после a - da; после глухой k частица не меняется: da, не ta')], d: 2,
      },
      {
        kind: 'transform', source: 'Ben çay içerim.', sourceRu: 'я выпью чаю',
        instruction: 'Скажите "Я и чай выпью" (не только кофе)',
        answer: 'Ben çay da içerim.', variants: [],
        breakdown: [p('çay'), sp, p('da', 'и', 'встаёт сразу за своим словом: çay da - "и чай"')], d: 2,
      },
      {
        kind: 'transform', source: 'Kahve içmem.', sourceRu: 'я не пью кофе',
        instruction: 'Ответьте: "Я тоже"',
        answer: 'Ben de.', variants: [],
        breakdown: [p('Ben'), sp, p('de', 'тоже', 'Ben de отвечает и на да, и на нет')], d: 2,
      },
      {
        kind: 'transform', source: 'Её сестра тоже идёт?', instruction: 'Скажите это по-турецки',
        answer: 'Ablası da geliyor mu?', variants: [], blankWhole: true,
        breakdown: [p('Ablası'), sp, p('da', 'тоже', 'последняя гласная ı - da')], d: 3,
      },
      {
        kind: 'transform', source: 'Дома и молока нет.', instruction: 'Скажите это по-турецки',
        answer: 'Evde süt de yok.', variants: [], blankWhole: true,
        breakdown: [p('süt'), sp, p('de', 'и, тоже', 'после ü - de; после глухой t - всё равно de, не te')], d: 3,
      },
      {
        kind: 'transform', source: 'И отель красивый.', instruction: 'Скажите это по-турецки',
        answer: 'Otel de güzel.', variants: [], blankWhole: true,
        breakdown: [p('Otel'), sp, p('de', 'и, тоже', 'последняя гласная e - de')], d: 3,
      },
    ],
  },

  // a2-l012: hem… hem de, ne… ne de, ya… ya da.
  {
    rule: 'correlative-hem-ne',
    explain:
      'Парные связки: hem... hem de - "и..., и...", ne... ne de - "ни..., ни...", ya... ya da - "либо..., либо...". Отрицание уже сидит в самих ne, поэтому глагол остаётся без -me: Ne çay ne de kahve içerim.',
    examples: [
      { parts: [p('Otel'), sp, p('hem', 'и...', ''), p(' ucuz '), p('hem de', '...и', 'de - частица "тоже", пишется отдельно'), p(' güzel.')], ru: 'Отель и дешёвый, и красивый.' },
      { parts: [p('Evde'), sp, p('ne', 'ни...', ''), p(' ekmek '), p('ne de', '...ни', ''), p(' süt '), p('var', 'без отрицания', 'отрицание уже в ne - var, не yok'), p('.')], ru: 'Дома нет ни хлеба, ни молока.' },
    ],
    items: [
      {
        kind: 'transform', source: 'Otel ucuz ve güzel.', sourceRu: 'отель дешёвый и красивый',
        instruction: 'Скажите "и..., и..." с hem... hem de',
        answer: 'Otel hem ucuz hem de güzel.', variants: ['Otel hem ucuz hem güzel.'],
        breakdown: [p('hem', 'и...', ''), p(' ucuz '), p('hem de', '...и', 'второе hem - с de')], d: 1,
      },
      {
        kind: 'transform', source: 'Bugün ya da yarın gelirim.', sourceRu: 'приду сегодня или завтра',
        instruction: 'Скажите "либо..., либо..." с ya... ya da',
        answer: 'Ya bugün ya da yarın gelirim.', variants: ['Ya bugün ya yarın gelirim.'],
        breakdown: [p('Ya', 'либо...', ''), p(' bugün '), p('ya da', '...либо', 'da по гармонии после ya')], d: 1,
      },
      {
        kind: 'transform', source: 'Ali yemek pişirecek, tatlı da getirecek.', sourceRu: 'Али приготовит еду, и десерт тоже принесёт',
        instruction: 'Скажите с hem... hem de: "Али и приготовит, и десерт принесёт"',
        answer: 'Ali hem yemek pişirecek hem de tatlı getirecek.', variants: ['Ali hem yemek pişirecek hem tatlı getirecek.'],
        breakdown: [p('hem de', '...и', 'вторая половина пары')], d: 1,
      },
      {
        kind: 'transform', source: 'Çay içmem, kahve de içmem.', sourceRu: 'чай не пью, и кофе тоже не пью',
        instruction: 'Скажите одной фразой: Ne çay ne de kahve ...',
        answer: 'Ne çay ne de kahve içerim.', variants: ['Ne çay ne kahve içerim.'],
        breakdown: [p('iç'), p('er', 'широкое время', 'глагол утвердительный: отрицание уже в ne'), p('im', 'я', '')], d: 2,
      },
      {
        kind: 'transform', source: 'Evde ekmek yok, süt de yok.', sourceRu: 'дома нет хлеба, и молока тоже нет',
        instruction: 'Скажите одной фразой: Evde ne ekmek ne de süt ...',
        answer: 'Evde ne ekmek ne de süt var.', variants: ['Evde ne ekmek ne süt var.'],
        breakdown: [p('var', 'без отрицания', 'отрицание уже в ne - var, не yok')], d: 2,
      },
      {
        kind: 'transform', source: 'Ben gitmedim, o da gitmedi.', sourceRu: 'я не пошёл, и он тоже',
        instruction: 'Скажите одной фразой: Ne ben ... ne de o.',
        answer: 'Ne ben gittim ne de o.', variants: ['Ne ben gittim ne o.'],
        breakdown: [p('git'), p('ti', 'прошедшее (-di)', 'без -me: отрицание уже в ne; после глухой t - t'), p('m', 'я', '')], d: 2,
      },
      {
        kind: 'transform', source: 'И дёшево, и красиво.', instruction: 'Скажите это по-турецки',
        answer: 'Hem ucuz hem de güzel.', variants: ['Hem ucuz hem güzel.'], blankWhole: true,
        breakdown: [p('Hem', 'и...', ''), p(' ucuz '), p('hem de', '...и', ''), p(' güzel')], d: 3,
      },
      {
        kind: 'transform', source: 'Приду либо сегодня, либо завтра.', instruction: 'Скажите это по-турецки',
        answer: 'Ya bugün ya da yarın gelirim.', variants: ['Ya bugün ya yarın gelirim.'], blankWhole: true,
        breakdown: [p('Ya', 'либо...', ''), p(' bugün '), p('ya da', '...либо', ''), p(' yarın')], d: 3,
      },
      {
        kind: 'transform', source: 'Я не пью ни чая, ни кофе.', instruction: 'Скажите это по-турецки',
        answer: 'Ne çay ne de kahve içerim.', variants: ['Ne çay ne kahve içerim.', 'Ben ne çay ne de kahve içerim.'], blankWhole: true,
        breakdown: [p('Ne', 'ни...', ''), p(' çay '), p('ne de', '...ни', ''), p(' kahve '), p('içerim', 'без -me', 'отрицание уже в ne - içerim, не içmem')], d: 3,
      },
    ],
  },

  // a2-l016: -ce / -ca «как? по-какому?».
  {
    rule: 'suffix-ce-manner',
    explain:
      'Türk + -çe = "по-турецки", как русское "по-...-ски". Тот же хвост делает из прилагательного наречие "как?": sakin - sakince, açık - açıkça. Вариантов четыре: -ce / -ca по гармонии, а после глухих согласных - -çe / -ça.',
    examples: [
      { parts: [p('Türk'), p('çe', 'по-какому?', 'после глухой k - ç; последняя гласная ü - e'), p(' biliyorum.')], ru: 'Я знаю турецкий.' },
      { parts: [p('Kibar'), p('ca', 'как?', 'r звонкая - c; последняя гласная a - a'), p(' cevap verdi.')], ru: 'Он вежливо ответил.' },
    ],
    items: [
      {
        kind: 'affix', base: 'Rus', baseRu: 'русский (народ)', task: 'по-каковски?',
        context: { tr: 'Evde ___ konuşuyoruz.', ru: 'Дома мы говорим по-русски.' },
        slots: [stem('Rus'), { answer: 'ça', options: ['ça', 'ca', 'çe'], label: 'по-какому?', why: 'после глухой s - ç; последняя гласная u - a' }],
        answer: 'Rusça', d: 1,
      },
      {
        kind: 'affix', base: 'İngiliz', baseRu: 'англичанин', task: 'по-каковски?',
        context: { tr: '___ anlıyorum.', ru: 'Я понимаю по-английски.' },
        slots: [stem('İngiliz'), { answer: 'ce', options: ['ce', 'ca', 'çe'], label: 'по-какому?', why: 'z звонкая - c; последняя гласная i - e' }],
        answer: 'İngilizce', d: 1,
      },
      {
        kind: 'affix', base: 'Alman', baseRu: 'немец', task: 'по-каковски?',
        context: { tr: '___ bilmiyorum.', ru: 'Я не знаю немецкого.' },
        slots: [stem('Alman'), { answer: 'ca', options: ['ca', 'ça', 'ce'], label: 'по-какому?', why: 'n звонкая - c; последняя гласная a - a' }],
        answer: 'Almanca', d: 1,
      },
      {
        kind: 'affix', base: 'kibar', baseRu: 'вежливый', task: 'как?',
        context: { tr: 'Garson ___ cevap verdi.', ru: 'Официант вежливо ответил.' },
        slots: [stem('kibar'), { answer: 'ca', options: ['ca', 'ce', 'ça'], label: 'как?', why: 'r звонкая - c; последняя гласная a - a' }],
        answer: 'kibarca', d: 1,
      },
      {
        kind: 'affix', base: 'açık', baseRu: 'открытый', task: 'как?',
        context: { tr: '___ söylüyorum.', ru: 'Говорю прямо.' },
        slots: [stem('açık'), { answer: 'ça', options: ['ça', 'ca', 'çe'], label: 'как?', why: 'после глухой k - ç, как mutfakta' }],
        answer: 'açıkça', d: 2,
      },
      {
        kind: 'affix', base: 'dürüst', baseRu: 'честный', task: 'как?',
        context: { tr: '___ söyleyeceğim.', ru: 'Скажу честно.' },
        slots: [stem('dürüst'), { answer: 'çe', options: ['çe', 'ce', 'ça'], label: 'как?', why: 'после глухой t - ç; последняя гласная ü - e' }],
        answer: 'dürüstçe', d: 2,
      },
      {
        kind: 'affix', base: 'iyi', baseRu: 'хороший', task: 'как следует',
        context: { tr: '___ düşündüm.', ru: 'Я как следует подумал.' },
        slots: [stem('iyi'), { answer: 'ce', options: ['ce', 'ca', 'çe'], label: 'как?', why: 'iyice - "как следует, основательно", а не "хорошо"' }],
        answer: 'iyice', d: 2,
      },
      {
        kind: 'transform', source: 'Я знаю турецкий.', instruction: 'Скажите это по-турецки',
        answer: 'Türkçe biliyorum.', variants: ['Ben Türkçe biliyorum.'], blankWhole: true,
        breakdown: [p('Türk'), p('çe', 'по-какому?', 'после глухой k - ç')], d: 3,
      },
      {
        kind: 'transform', source: 'Он вежливо ответил.', instruction: 'Скажите это по-турецки',
        answer: 'Kibarca cevap verdi.', variants: ['O kibarca cevap verdi.'], blankWhole: true,
        breakdown: [p('Kibar'), p('ca', 'как?', 'в вежливой манере')], d: 3,
      },
      {
        kind: 'transform', source: 'Он потихоньку открыл дверь.', instruction: 'Скажите это по-турецки',
        answer: 'Kapıyı yavaşça açtı.', variants: ['O kapıyı yavaşça açtı.', 'Yavaşça kapıyı açtı.'], blankWhole: true,
        breakdown: [p('yavaş'), p('ça', 'как?', 'после глухой ş - ç')], d: 3,
      },
    ],
  },

  // a2-l018: biri, bir şey, hiç kimse, hiçbir şey, herkes, her şey.
  {
    rule: 'indefinite-pronouns',
    explain:
      'Про людей: biri "кто-то", herkes "все", hiç kimse "никто"; про вещи: bir şey, her şey, hiçbir şey. С "никто" и "ничего" глагол тоже ставят в отрицание: Hiç kimse gelmedi. Herkes и her şey - единственное число: Herkes geldi.',
    examples: [
      { parts: [p('Hiç kimse '), p('gel'), p('me', 'отрицание', 'с hiç kimse глагол тоже с "не"'), p('di.')], ru: 'Никто не пришёл.' },
      { parts: [p('Herkes '), p('çay iç'), p('iyor', 'он', 'herkes - единственное число: içiyor, не içiyorlar'), p('.')], ru: 'Все пьют чай.' },
    ],
    items: [
      {
        kind: 'affix', base: 'biri', baseRu: 'кто-то', task: 'кого? (винительный падеж)',
        context: { tr: 'Parkta ___ gördüm.', ru: 'Я видел кого-то в парке.' },
        slots: [stem('biri'), { answer: 'ni', options: ['ni', 'yi', 'i'], label: 'кого? (винительный падеж)', why: 'как evini: после -i перед падежом - n' }],
        answer: 'birini', d: 1,
      },
      {
        kind: 'affix', base: 'herkes', baseRu: 'все', task: 'кому?',
        context: { tr: '___ söyledim.', ru: 'Я всем сказал.' },
        slots: [stem('herkes'), { answer: 'e', options: ['e', 'i', 'de'], label: 'кому?', why: 'падежи - как у обычного слова: herkese' }],
        answer: 'herkese', d: 1,
      },
      {
        kind: 'affix', base: 'her şey', baseRu: 'всё', task: 'что? (винительный падеж)',
        context: { tr: '___ aldım.', ru: 'Я купил всё.' },
        slots: [stem('her şey'), { answer: 'i', options: ['i', 'yi', 'e'], label: 'что? (винительный падеж)', why: '"всё" - вполне определённое, хвост нужен' }],
        answer: 'her şeyi', d: 1,
      },
      {
        kind: 'transform', source: 'Biri geldi.', sourceRu: 'кто-то пришёл',
        instruction: 'Скажите "Никто не пришёл"',
        answer: 'Hiç kimse gelmedi.', variants: ['Kimse gelmedi.'],
        breakdown: [p('Hiç kimse', 'никто', ''), sp, p('gel'), p('me', 'отрицание', 'с "никто" глагол тоже с "не"'), p('di')], d: 1,
      },
      {
        kind: 'transform', source: 'Ali geldi, Elif geldi, Ayşe geldi.', sourceRu: 'пришли Али, Элиф и Айше',
        instruction: 'Скажите "Все пришли": Herkes ...',
        answer: 'Herkes geldi.', variants: [],
        breakdown: [p('gel'), p('di', 'он', 'herkes - единственное число: geldi, не geldiler')], d: 2,
      },
      {
        kind: 'transform', source: 'Bir şey aldım.', sourceRu: 'я кое-что купил',
        instruction: 'Скажите "Я ничего не купил": Hiçbir şey ...',
        answer: 'Hiçbir şey almadım.', variants: ['Ben hiçbir şey almadım.'],
        breakdown: [p('al'), p('ma', 'отрицание', 'с hiçbir şey глагол тоже с "не"'), p('dım', 'я', '')], d: 2,
      },
      {
        kind: 'transform', source: 'Evde biri var mı?', sourceRu: 'дома кто-нибудь есть?',
        instruction: 'Ответьте: "Дома никого нет"',
        answer: 'Evde hiç kimse yok.', variants: ['Evde kimse yok.'],
        breakdown: [p('hiç kimse', 'никто', 'с yok отрицание уже внутри yok')], d: 2,
      },
      {
        kind: 'transform', source: 'Никто не пришёл.', instruction: 'Скажите это по-турецки',
        answer: 'Hiç kimse gelmedi.', variants: ['Kimse gelmedi.'], blankWhole: true,
        breakdown: [p('Hiç kimse', 'никто', ''), sp, p('gel'), p('me', 'отрицание', ''), p('di')], d: 3,
      },
      {
        kind: 'transform', source: 'Я ничего не купил.', instruction: 'Скажите это по-турецки',
        answer: 'Hiçbir şey almadım.', variants: ['Ben hiçbir şey almadım.'], blankWhole: true,
        breakdown: [p('Hiçbir şey', 'ничего', ''), sp, p('al'), p('ma', 'отрицание', ''), p('dım')], d: 3,
      },
      {
        kind: 'transform', source: 'Все пьют чай.', instruction: 'Скажите это по-турецки',
        answer: 'Herkes çay içiyor.', variants: [], blankWhole: true,
        breakdown: [p('Herkes', 'все', ''), p(' çay '), p('iç'), p('iyor', 'он', 'herkes - единственное: içiyor, не içiyorlar')], d: 3,
      },
    ],
  },

  // a2-l019: -ki «тот, что…».
  {
    rule: 'suffix-ki-relative',
    explain:
      'К месту добавляем -ki: masada "на столе" - masadaki bardak "стакан, что на столе". Со временем так же: bugün - bugünkü hava. -ki превращает ответ на "где?" или "когда?" в ответ на "какой?" и стоит перед словом.',
    examples: [
      { parts: [p('Masa'), p('da', 'где?', ''), p('ki', 'какой?', 'почти всегда -ki; стоит перед словом'), p(' bardak')], ru: 'стакан, что на столе' },
      { parts: [p('Bugün'), p('kü', 'какой?', 'исключение: bugünkü, dünkü'), p(' hava güzel.')], ru: 'Сегодняшняя погода хорошая.' },
    ],
    items: [
      {
        kind: 'affix', base: 'dolap', baseRu: 'шкаф', task: 'где? · какой? (-ki)',
        context: { tr: '___ havlular kuru.', ru: 'Полотенца в шкафу сухие.' },
        slots: [stem('dolap'), { answer: 'ta', options: ['ta', 'da', 'te'], label: 'где?', why: 'после глухой p - t' }, { answer: 'ki', options: ['ki', 'kı', 'ku'], label: 'какой?', why: '-ki не меняется по гармонии (только после ü бывает -kü: bugünkü, dünkü)' }],
        answer: 'dolaptaki', d: 1,
      },
      {
        kind: 'affix', base: 'ev', baseRu: 'дом', task: 'где? · какой? (-ki)',
        context: { tr: '___ kitaplar çok ağır.', ru: 'Книги, что дома, очень тяжёлые.' },
        slots: [stem('ev'), { answer: 'de', options: ['de', 'da', 'e'], label: 'где?', why: '' }, { answer: 'ki', options: ['ki', 'kı', 'kü'], label: 'какой?', why: 'место - "какой?": evdeki' }],
        answer: 'evdeki', d: 1,
      },
      {
        kind: 'affix', base: 'yarın', baseRu: 'завтра', task: 'когда? · какой? (-ki)',
        context: { tr: '___ toplantı önemli.', ru: 'Завтрашнее совещание важное.' },
        slots: [stem('yarın'), { answer: 'ki', options: ['ki', 'kı', 'de'], label: 'какой?', why: 'слова времени берут -ki сразу, без -de' }],
        answer: 'yarınki', d: 1,
      },
      {
        kind: 'affix', base: 'mutfak', baseRu: 'кухня', task: 'где? · какой? (-ki)',
        context: { tr: '___ masa yuvarlak.', ru: 'Стол на кухне круглый.' },
        slots: [stem('mutfak'), { answer: 'ta', options: ['ta', 'da', 'te'], label: 'где?', why: 'после глухой k - t' }, { answer: 'ki', options: ['ki', 'kı', 'ku'], label: 'какой?', why: '-ki, не "kı"' }],
        answer: 'mutfaktaki', d: 1,
      },
      {
        kind: 'affix', base: 'bugün', baseRu: 'сегодня', task: 'когда? · какой? (-ki)',
        context: { tr: '___ hava güzel.', ru: 'Сегодняшняя погода хорошая.' },
        slots: [stem('bugün'), { answer: 'kü', options: ['kü', 'ki', 'de'], label: 'какой?', why: 'исключение: bugünkü, dünkü' }],
        answer: 'bugünkü', d: 2,
      },
      {
        kind: 'affix', base: 'çantam', baseRu: 'моя сумка', task: 'где? · какой? (-ki)',
        context: { tr: '___ kitaplar ıslak.', ru: 'Книги в моей сумке мокрые.' },
        slots: [stem('çantam'), { answer: 'da', options: ['da', 'de', 'ta'], label: 'где?', why: '' }, { answer: 'ki', options: ['ki', 'kı', 'de'], label: 'какой?', why: 'çantamdaki - "те, что в моей сумке"' }],
        answer: 'çantamdaki', d: 2,
      },
      {
        kind: 'transform', source: 'Havlu banyoda.', sourceRu: 'полотенце - в ванной',
        instruction: 'Скажите "Полотенце в ванной мокрое"',
        answer: 'Banyodaki havlu ıslak.', variants: [],
        breakdown: [p('Banyo'), p('da', 'где?', ''), p('ki', 'какой?', '"какой" стоит перед словом: banyodaki havlu')], d: 2,
      },
      {
        kind: 'transform', source: 'Сегодняшняя погода хорошая.', instruction: 'Скажите это по-турецки',
        answer: 'Bugünkü hava güzel.', variants: [], blankWhole: true,
        breakdown: [p('Bugün'), p('kü', 'какой?', 'исключение: bugünkü')], d: 3,
      },
      {
        kind: 'transform', source: 'Сумка на столе - твоя?', instruction: 'Скажите это по-турецки',
        answer: 'Masadaki çanta senin mi?', variants: [], blankWhole: true,
        breakdown: [p('Masa'), p('da', 'где?', ''), p('ki', 'какой?', 'стоит перед словом, не "çanta masada"')], d: 3,
      },
      {
        kind: 'transform', source: 'Hangi havlu?', sourceRu: 'какое полотенце?',
        instruction: 'Ответьте одним словом: "То, что в шкафу"',
        answer: 'Dolaptaki.', variants: [], blankWhole: true,
        breakdown: [p('Dolap'), p('ta', 'где?', ''), p('ki', 'какой?', '-ki заменяет само слово: "тот, что в шкафу"')], d: 3,
      },
    ],
  },

  // a2-l020: hiç, henüz, artık, hâlâ.
  {
    rule: 'negative-polarity-adverbs',
    explain:
      'henüz - "ещё не": события пока не было; hâlâ - "всё ещё": всё как было; artık - "уже" или "больше не": всё изменилось; hiç - "совсем не, ни разу". henüz и hiç живут с отрицанием: Henüz temizlemedim, Hiç yorgun değilim.',
    examples: [
      { parts: [p('Henüz', 'ещё не', 'только с отрицанием'), p(' temizle'), p('me', 'отрицание', ''), p('dim.')], ru: 'Я ещё не убрал.' },
      { parts: [p('Oda '), p('artık', 'теперь', 'новое положение дел'), p(' temiz.')], ru: 'В комнате теперь чисто.' },
    ],
    items: [
      {
        kind: 'transform', source: 'Oda kirli.', sourceRu: 'в комнате грязно',
        instruction: 'Скажите "В комнате всё ещё грязно"',
        answer: 'Oda hâlâ kirli.', variants: ['Oda hala kirli.'],
        breakdown: [p('hâlâ', 'всё ещё', 'ничего не изменилось')], d: 1,
      },
      {
        kind: 'transform', source: 'Oda kirliydi.', sourceRu: 'в комнате было грязно',
        instruction: 'Скажите "Теперь чисто"',
        answer: 'Artık temiz.', variants: ['Oda artık temiz.'],
        breakdown: [p('Artık', 'теперь, уже', 'всё изменилось')], d: 1,
      },
      {
        kind: 'transform', source: 'Yorgun değilim.', sourceRu: 'я не устал',
        instruction: 'Скажите "Я совсем не устал"',
        answer: 'Hiç yorgun değilim.', variants: ['Ben hiç yorgun değilim.'],
        breakdown: [p('Hiç', 'совсем не', 'с отрицанием')], d: 1,
      },
      {
        kind: 'transform', source: 'Mutfağı temizledin mi?', sourceRu: 'ты убрал кухню?',
        instruction: 'Ответьте: "Ещё не убрал"',
        answer: 'Henüz temizlemedim.', variants: ['Ben henüz temizlemedim.', 'Hayır, henüz temizlemedim.'],
        breakdown: [p('Henüz', 'ещё не', 'только с отрицанием'), sp, p('temizle'), p('me', 'отрицание', ''), p('dim', 'я', '')], d: 2,
      },
      {
        kind: 'transform', source: 'Ev hâlâ gürültülü mü?', sourceRu: 'в квартире всё ещё шумно?',
        instruction: 'Ответьте: "Нет, больше не шумно"',
        answer: 'Hayır, artık gürültülü değil.', variants: ['Artık gürültülü değil.', 'Hayır, artık değil.'],
        breakdown: [p('artık', 'больше не', 'artık с отрицанием - "больше не"')], d: 2,
      },
      {
        kind: 'transform', source: 'Kahve içiyorum.', sourceRu: 'я пью кофе',
        instruction: 'Скажите "Я больше не пью кофе"',
        answer: 'Artık kahve içmiyorum.', variants: ['Ben artık kahve içmiyorum.'],
        breakdown: [p('Artık', 'больше не', 'с отрицанием'), p(' kahve '), p('iç'), p('mi', 'отрицание', ''), p('yorum')], d: 2,
      },
      {
        kind: 'transform', source: 'İstanbul\'a gittin mi?', sourceRu: 'ты ездил в Стамбул?',
        instruction: 'Спросите: "Ты когда-нибудь был в Стамбуле?"',
        answer: 'İstanbul\'a hiç gittin mi?', variants: ['Hiç İstanbul\'a gittin mi?'],
        breakdown: [p('hiç', 'когда-нибудь', 'в вопросе hiç - "когда-нибудь"')], d: 2,
      },
      {
        kind: 'transform', source: 'Он всё ещё спит.', instruction: 'Скажите это по-турецки',
        answer: 'Hâlâ uyuyor.', variants: ['O hâlâ uyuyor.'], blankWhole: true,
        breakdown: [p('Hâlâ', 'всё ещё', ''), sp, p('uyu'), p('yor')], d: 3,
      },
      {
        kind: 'transform', source: 'Я ещё не ел.', instruction: 'Скажите это по-турецки',
        answer: 'Henüz yemedim.', variants: ['Ben henüz yemedim.'], blankWhole: true,
        breakdown: [p('Henüz', 'ещё не', 'henüz - только "ещё не", с отрицанием'), sp, p('ye'), p('me', 'отрицание', ''), p('dim')], d: 3,
      },
      {
        kind: 'transform', source: 'Я там ни разу не был.', instruction: 'Скажите это по-турецки',
        answer: 'Hiç gitmedim.', variants: ['Ben hiç gitmedim.', 'Oraya hiç gitmedim.'], blankWhole: true,
        breakdown: [p('Hiç', 'ни разу', 'с отрицанием'), sp, p('git'), p('me', 'отрицание', ''), p('dim')], d: 3,
      },
    ],
  },


  // a2-l024: -mek zorundayım против -malıyım.
  {
    rule: 'zorunda-strong-necessity',
    explain:
      'Gitmeliyim - "мне следует пойти": я сам так считаю. Gitmek zorundayım - "я вынужден пойти": выбора нет, так требуют обстоятельства. Глагол целиком на -mek, а лицо - на слове zorunda: zorundayım, zorundasın, zorunda.',
    examples: [
      { parts: [p('Çalışmak '), p('zorunda', 'вынужден', 'глагол целиком на -mek'), p('yım', 'я', 'лицо - на zorunda')], ru: 'Я вынужден работать.' },
      { parts: [p('Gitmek '), p('zorunda', 'обязан', ''), p(' değil', 'не', 'не обязан, но можно'), p('sin', 'ты', '')], ru: 'Ты не обязан идти (можешь остаться).' },
    ],
    items: [
      {
        kind: 'affix', base: 'zorunda', baseRu: 'вынужден', task: 'я',
        context: { tr: 'Çalışmak ___.', ru: 'Я вынужден работать.' },
        slots: [stem('zorunda'), { answer: 'yım', options: ['yım', 'ım', 'yim'], label: 'я', why: 'после гласной - y; последняя a - -yım' }],
        answer: 'zorundayım', d: 1,
      },
      {
        kind: 'affix', base: 'zorunda', baseRu: 'вынужден', task: 'ты',
        context: { tr: 'Erken kalkmak ___.', ru: 'Тебе придётся встать рано.' },
        slots: [stem('zorunda'), { answer: 'sın', options: ['sın', 'sin', 'yım'], label: 'ты', why: 'последняя гласная a - -sın' }],
        answer: 'zorundasın', d: 1,
      },
      {
        kind: 'affix', base: 'zorunda', baseRu: 'вынужден', task: 'мы',
        context: { tr: 'Pasaport göstermek ___.', ru: 'Мы обязаны показать паспорт.' },
        slots: [stem('zorunda'), { answer: 'yız', options: ['yız', 'ız', 'yiz'], label: 'мы', why: 'после гласной - y; последняя a - -yız' }],
        answer: 'zorundayız', d: 1,
      },
      {
        kind: 'affix', base: 'zorunda', baseRu: 'вынужден', task: 'вы',
        context: { tr: 'Bir hafta ilaç almak ___.', ru: 'Вы обязаны принимать лекарство неделю.' },
        slots: [stem('zorunda'), { answer: 'sınız', options: ['sınız', 'siniz', 'yız'], label: 'вы', why: 'последняя гласная a - -sınız' }],
        answer: 'zorundasınız', d: 1,
      },
      {
        kind: 'transform', source: 'İşe gitmek zorundayım.', sourceRu: 'я обязан идти на работу',
        instruction: 'Спросите: "Мне обязательно идти на работу?"',
        answer: 'İşe gitmek zorunda mıyım?', variants: [],
        breakdown: [p('zorunda'), sp, p('mı', 'вопрос', 'mi пишется отдельно'), p('yım', 'я', '')], d: 2,
      },
      {
        kind: 'transform', source: 'Gitmek zorundasın.', sourceRu: 'ты обязан идти',
        instruction: 'Скажите "Ты не обязан идти" (можешь остаться)',
        answer: 'Gitmek zorunda değilsin.', variants: [],
        breakdown: [p('zorunda'), p(' değil', 'не', '"не обязан" - zorunda değil'), p('sin', 'ты', '')], d: 2,
      },
      {
        kind: 'transform', source: 'Gitmek zorunda değilsin.', sourceRu: 'ты не обязан идти',
        instruction: 'Скажите другое: "Тебе не стоит идти" (лучше не надо)',
        answer: 'Gitmemelisin.', variants: [],
        breakdown: [p('Git'), p('me', 'отрицание', '"не стоит" - -meme-, а не zorunda değil'), p('meli', 'следует', ''), p('sin', 'ты', '')], d: 2,
      },
      {
        kind: 'transform', source: 'İlaç almalıyım.', sourceRu: 'мне стоит принять лекарство',
        instruction: 'Выбора нет, так велел врач: "Я обязан принимать лекарство"',
        answer: 'İlaç almak zorundayım.', variants: [],
        breakdown: [p('al'), p('mak', '-mak', 'глагол целиком'), p(' zorunda', 'вынужден', ''), p('yım', 'я', '')], d: 2,
      },
      {
        kind: 'transform', source: 'Я вынужден работать.', instruction: 'Скажите это по-турецки',
        answer: 'Çalışmak zorundayım.', variants: ['Ben çalışmak zorundayım.'], blankWhole: true,
        breakdown: [p('Çalışmak '), p('zorunda', 'вынужден', ''), p('yım', 'я', '')], d: 3,
      },
      {
        kind: 'transform', source: 'Ты не обязан идти.', instruction: 'Скажите это по-турецки',
        answer: 'Gitmek zorunda değilsin.', variants: ['Sen gitmek zorunda değilsin.'], blankWhole: true,
        breakdown: [p('Gitmek '), p('zorunda', 'обязан', ''), p(' değil', 'не', ''), p('sin', 'ты', '')], d: 3,
      },
      {
        kind: 'transform', source: 'Мне пришлось уйти.', instruction: 'Скажите это по-турецки',
        answer: 'Gitmek zorunda kaldım.', variants: [], blankWhole: true,
        breakdown: [p('Gitmek '), p('zorunda', 'вынужден', ''), p(' kaldım', 'пришлось', 'прошлое: zorunda kaldım')], d: 3,
      },
    ],
  },

  // a2-l029: bazı, birkaç, her, bütün.
  {
    rule: 'quantifiers',
    explain:
      'bazı - "некоторые", birkaç - "несколько", her - "каждый", bütün - "все". После bazı и bütün - множественное число (bazı köpekler), после birkaç и her - единственное (birkaç köpek, her köpek), хотя по-русски "несколько собак".',
    examples: [
      { parts: [p('Parkta '), p('birkaç', 'несколько', 'после birkaç - единственное число'), p(' köpek var.')], ru: 'В парке несколько собак.' },
      { parts: [p('Bütün '), p('kedi'), p('ler', 'множественное число', 'после bütün - множественное'), p(' süt sever.')], ru: 'Все кошки любят молоко.' },
    ],
    items: [
      {
        kind: 'transform', source: 'Kediler süt sever.', sourceRu: 'кошки любят молоко',
        instruction: 'Скажите "Все кошки любят молоко" (bütün)',
        answer: 'Bütün kediler süt sever.', variants: [],
        breakdown: [p('Bütün', 'все', ''), sp, p('kedi'), p('ler', 'множественное число', 'после bütün - множественное')], d: 1,
      },
      {
        kind: 'transform', source: 'Yılanlar ısırır.', sourceRu: 'змеи кусаются',
        instruction: 'Скажите "Некоторые змеи кусаются" (bazı)',
        answer: 'Bazı yılanlar ısırır.', variants: [],
        breakdown: [p('Bazı', 'некоторые', ''), sp, p('yılan'), p('lar', 'множественное число', 'после bazı - множественное')], d: 1,
      },
      {
        kind: 'transform', source: 'Tavşan hızlı koşar.', sourceRu: 'заяц быстро бегает',
        instruction: 'Скажите "Каждый заяц быстро бегает" (her)',
        answer: 'Her tavşan hızlı koşar.', variants: [],
        breakdown: [p('Her', 'каждый', 'после her - единственное число'), p(' tavşan')], d: 1,
      },
      {
        kind: 'transform', source: 'Parkta köpekler var.', sourceRu: 'в парке есть собаки',
        instruction: 'Скажите "несколько собак" (birkaç)',
        answer: 'Parkta birkaç köpek var.', variants: ['Parkta birkaç tane köpek var.'],
        breakdown: [p('birkaç', 'несколько', 'после birkaç - единственное: köpek, не köpekler'), p(' köpek')], d: 2,
      },
      {
        kind: 'transform', source: 'Su içtim.', sourceRu: 'я попил воды',
        instruction: 'Скажите "Я выпил немного воды": birkaç или biraz?',
        answer: 'Biraz su içtim.', variants: [],
        breakdown: [p('Biraz', 'немного', 'воду не считают штуками - biraz, не birkaç')], d: 2,
      },
      {
        kind: 'transform', source: 'Evdeyim.', sourceRu: 'я дома',
        instruction: 'Скажите "Я весь день дома"',
        answer: 'Bütün gün evdeyim.', variants: [],
        breakdown: [p('Bütün', 'целый', 'bütün с единственным числом - "целый, весь"'), p(' gün')], d: 2,
      },
      {
        kind: 'transform', source: 'Bu örümcek ısırır mı?', sourceRu: 'этот паук кусается?',
        instruction: 'Ответьте глаголом: "Не кусается"',
        answer: 'Isırmaz.', variants: ['Hayır, ısırmaz.'],
        breakdown: [p('Isır'), p('maz', 'не (широкое)', 'короткий ответ - сам глагол')], d: 2,
      },
      {
        kind: 'transform', source: 'В парке несколько собак.', instruction: 'Скажите это по-турецки',
        answer: 'Parkta birkaç köpek var.', variants: ['Parkta birkaç tane köpek var.'], blankWhole: true,
        breakdown: [p('birkaç', 'несколько', ''), p(' köpek')], d: 3,
      },
      {
        kind: 'transform', source: 'Каждый заяц очень милый.', instruction: 'Скажите это по-турецки',
        answer: 'Her tavşan çok tatlı.', variants: [], blankWhole: true,
        breakdown: [p('Her', 'каждый', ''), p(' tavşan')], d: 3,
      },
      {
        kind: 'transform', source: 'Некоторые пауки кусаются.', instruction: 'Скажите это по-турецки',
        answer: 'Bazı örümcekler ısırır.', variants: [], blankWhole: true,
        breakdown: [p('Bazı', 'некоторые', ''), sp, p('örümcek'), p('ler', 'множественное число', '')], d: 3,
      },
    ],
  },

  // a2-l030: günde iki kez.
  {
    rule: 'frequency-per-period',
    explain:
      '"Два раза в день" по-турецки - günde iki kez: сначала период с окончанием "в" (-de), потом сколько раз. Gün - günde "в день", hafta - haftada "в неделю", ay - ayda "в месяц", yıl - yılda "в год".',
    examples: [
      { parts: [p('Gün'), p('de', 'в (день)', 'местный падеж: "внутри одного дня"'), p(' iki kez kahve içerim.')], ru: 'Я пью кофе два раза в день.' },
      { parts: [p('Hafta'), p('da', 'в (неделю)', 'период идёт первым'), p(' üç gün koşarım.')], ru: 'Я бегаю три дня в неделю.' },
    ],
    items: [
      {
        kind: 'affix', base: 'gün', baseRu: 'день', task: 'в день',
        context: { tr: '___ iki kez kahve içerim.', ru: 'Я пью кофе два раза в день.' },
        slots: [stem('gün'), { answer: 'de', options: ['de', 'e', 'da'], label: 'в (период)', why: 'местный падеж: günde, не güne' }],
        answer: 'günde', d: 1,
      },
      {
        kind: 'affix', base: 'hafta', baseRu: 'неделя', task: 'в неделю',
        context: { tr: '___ üç gün koşarım.', ru: 'Я бегаю три дня в неделю.' },
        slots: [stem('hafta'), { answer: 'da', options: ['da', 'ta', 'de'], label: 'в (период)', why: 'местный падеж; после гласной a - -da' }],
        answer: 'haftada', d: 1,
      },
      {
        kind: 'affix', base: 'yıl', baseRu: 'год', task: 'в год',
        context: { tr: '___ bir kez tatile gideriz.', ru: 'Раз в год мы ездим в отпуск.' },
        slots: [stem('yıl'), { answer: 'da', options: ['da', 'a', 'de'], label: 'в (период)', why: 'местный падеж: yılda' }],
        answer: 'yılda', d: 1,
      },
      {
        kind: 'affix', base: 'ay', baseRu: 'месяц', task: 'в месяц',
        context: { tr: '___ bir kez dağa giderim.', ru: 'Раз в месяц я езжу в горы.' },
        slots: [stem('ay'), { answer: 'da', options: ['da', 'a', 'de'], label: 'в (период)', why: 'местный падеж: ayda, не aya' }],
        answer: 'ayda', d: 1,
      },
      {
        kind: 'affix', base: 'saat', baseRu: 'час', task: 'в час',
        context: { tr: '___ bir kez su içerim.', ru: 'Я пью воду раз в час.' },
        slots: [stem('saat'), { answer: 'te', options: ['te', 'de', 'ta'], label: 'в (период)', why: 'saat - как мягкое; после глухой t - -te' }],
        answer: 'saatte', d: 2,
      },
      {
        kind: 'transform', source: 'Her gün koşarım.', sourceRu: 'я бегаю каждый день',
        instruction: 'Скажите "Я бегаю два раза в день"',
        answer: 'Günde iki kez koşarım.', variants: ['Günde iki kere koşarım.'],
        breakdown: [p('Gün'), p('de', 'в (период)', 'за günde всегда стоит число; her gün - без числа'), p(' iki kez')], d: 2,
      },
      {
        kind: 'transform', source: 'Haftada beş gün çalışıyorum.', sourceRu: 'я работаю пять дней в неделю',
        instruction: 'Спросите: "Сколько дней в неделю ты работаешь?"',
        answer: 'Haftada kaç gün çalışıyorsun?', variants: ['Haftada kaç gün çalışırsın?'],
        breakdown: [p('Hafta'), p('da', 'в (период)', 'период - первым'), p(' kaç gün', 'сколько дней', '')], d: 2,
      },
      {
        kind: 'transform', source: 'Я пью кофе два раза в день.', instruction: 'Скажите это по-турецки',
        answer: 'Günde iki kez kahve içerim.', variants: ['Günde iki kez kahve içiyorum.', 'Ben günde iki kez kahve içerim.', 'Günde iki kere kahve içerim.'], blankWhole: true,
        breakdown: [p('Gün'), p('de', 'в (период)', ''), p(' iki kez', 'два раза', 'период, потом число')], d: 3,
      },
      {
        kind: 'transform', source: 'Раз в год мы ездим в отпуск.', instruction: 'Скажите это по-турецки',
        answer: 'Yılda bir kez tatile gideriz.', variants: ['Yılda bir tatile gideriz.', 'Yılda bir kere tatile gideriz.'], blankWhole: true,
        breakdown: [p('Yıl'), p('da', 'в (период)', ''), p(' bir kez', 'один раз', '')], d: 3,
      },
      {
        kind: 'transform', source: 'Я плаваю два раза в неделю.', instruction: 'Скажите это по-турецки',
        answer: 'Haftada iki kez yüzerim.', variants: ['Ben haftada iki kez yüzerim.', 'Haftada iki kere yüzerim.', 'Haftada iki kez yüzüyorum.'], blankWhole: true,
        breakdown: [p('Hafta'), p('da', 'в (период)', ''), p(' iki kez', 'два раза', '')], d: 3,
      },
    ],
  },
]
